import { useEffect, useState } from "react";
import { Loader2, Save, Upload, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";

export default function RootAppBranding() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/root/app-pwa/branding").then((r) => setB(r.data.branding)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const S = (k) => ({ value: b?.[k] ?? "", onChange: (e) => setB({ ...b, [k]: e.target.value }) });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/root/app-pwa/branding", b);
      toast.success("Branding saved — manifest updates immediately");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const uploadIcon = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    const rd = new FileReader();
    rd.onload = async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/root/app-pwa/branding/icon", { image: rd.result });
        toast.success(`Icon updated (v${data.icon_version}). ${data.note}`);
        load();
      } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
    };
    rd.readAsDataURL(f);
  };

  if (!b) return <LoadingRows rows={4} />;

  return (
    <div className="space-y-6" data-testid="root-app-branding">
      <PageHeader title="App & PWA Branding" subtitle="Launcher name, icons, colors and install banner for the installable MILTROO app" testid="app-branding-title" />
      <div className="max-w-2xl space-y-5">
        <div className="rounded-2xl border border-border p-5 flex items-center gap-5">
          <img src={b.icon_preview} alt="App icon" className="h-20 w-20 rounded-2xl shadow-lg" data-testid="app-icon-preview" />
          <div className="space-y-2">
            <p className="font-bold text-sm">PWA Launcher Icon</p>
            <p className="text-xs text-muted-foreground">Square PNG/JPEG/WebP, at least 192×192. All icon sizes + maskable variants are generated automatically.</p>
            <label className="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-sm cursor-pointer text-white" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }} data-testid="app-icon-upload-label">
              <Upload className="h-4 w-4" /> Upload Icon
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadIcon} data-testid="app-icon-upload" />
            </label>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>App Name</Label><Input data-testid="app-name" {...S("app_name")} /></div>
          <div className="space-y-1.5"><Label>Short Name</Label><Input data-testid="app-short-name" {...S("short_name")} /></div>
          <div className="space-y-1.5"><Label>Theme Color</Label><Input data-testid="app-theme-color" placeholder="#07110E" {...S("theme_color")} /></div>
          <div className="space-y-1.5"><Label>Background Color</Label><Input data-testid="app-bg-color" placeholder="#07110E" {...S("background_color")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Start URL (PWA launch page)</Label><Input data-testid="app-start-url" placeholder="/videos" {...S("start_url")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea rows={2} data-testid="app-description" {...S("description")} /></div>
        </div>

        <div className="rounded-2xl border border-border p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="font-bold text-sm flex items-center gap-2"><Smartphone className="h-4 w-4" />Install Banner</p>
              <p className="text-xs text-muted-foreground">Shown to website visitors when the app is not installed</p></div>
            <Switch data-testid="app-banner-enabled" checked={!!b.banner_enabled} onCheckedChange={(v) => setB({ ...b, banner_enabled: v })} />
          </div>
          <div className="space-y-1.5"><Label>Banner Title</Label><Input data-testid="app-banner-title" {...S("banner_title")} /></div>
          <div className="space-y-1.5"><Label>Banner Text</Label><Textarea rows={2} data-testid="app-banner-text" {...S("banner_text")} /></div>
        </div>

        <Button data-testid="app-branding-save" onClick={save} disabled={busy} className="rounded-full font-bold text-white border-0 px-8" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save & Publish</>}
        </Button>
        <p className="text-[11px] text-muted-foreground">Manifest and icon URLs are versioned automatically. Note: some browsers and operating systems may take time to refresh installed app icons, or may require reinstalling the app.</p>
      </div>
    </div>
  );
}
